import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DollarSign, Minus, Plus, User } from 'lucide-react';
import apiService from '../services/api';
import toast from 'react-hot-toast';

interface Employee {
  id: number;
  userId: number;
  type: 'FIXED' | 'METER';
  rate: number;
  user: {
    id: number;
    name: string;
    email: string;
  };
  advances: Advance[];
}

interface Advance {
  id: number;
  employeeId: number;
  amount: number;
  repaid: number;
  createdAt: string;
}

interface SalaryForm {
  employeeId: number;
  amount: number;
  reason: string;
}

interface AdvanceForm {
  employeeId: number;
  amount: number;
  reason: string;
}

const Salary: React.FC = () => {
  const { t } = useTranslation();
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
  const [isAdvanceModalOpen, setIsAdvanceModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const queryClient = useQueryClient();

  const {
    register: registerSalary,
    handleSubmit: handleSalarySubmit,
    reset: resetSalary,
    formState: { errors: salaryErrors },
  } = useForm<SalaryForm>();

  const {
    register: registerAdvance,
    handleSubmit: handleAdvanceSubmit,
    reset: resetAdvance,
    formState: { errors: advanceErrors },
  } = useForm<AdvanceForm>();

  // Fetch employees with advances
  const { data: employeesResponse, isLoading, error } = useQuery({
    queryKey: ['employees'],
    queryFn: () => apiService.get('/employees'),
  });
  
  const employees = employeesResponse?.employees || [];

  // Withdraw salary mutation
  const withdrawMutation = useMutation({
    mutationFn: (data: SalaryForm) => apiService.post('/salary/withdraw', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      toast.success('Salary withdrawn successfully');
      setIsWithdrawModalOpen(false);
      resetSalary();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to withdraw salary');
    },
  });

  // Add advance mutation
  const addAdvanceMutation = useMutation({
    mutationFn: (data: AdvanceForm) => apiService.post('/salary/advance', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      toast.success('Advance added successfully');
      setIsAdvanceModalOpen(false);
      resetAdvance();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to add advance');
    },
  });

  // Repay advance mutation
  const repayAdvanceMutation = useMutation({
    mutationFn: ({ advanceId, amount }: { advanceId: number; amount: number }) =>
      apiService.post(`/salary/advance/${advanceId}/repay`, { amount }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      toast.success('Advance repaid successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to repay advance');
    },
  });

  const onSalarySubmit = (data: SalaryForm) => {
    withdrawMutation.mutate(data);
  };

  const onAdvanceSubmit = (data: AdvanceForm) => {
    addAdvanceMutation.mutate(data);
  };

  const handleWithdrawSalary = (employee: Employee) => {
    setSelectedEmployee(employee);
    resetSalary({
      employeeId: employee.id,
      amount: 0,
      reason: '',
    });
    setIsWithdrawModalOpen(true);
  };

  const handleAddAdvance = (employee: Employee) => {
    setSelectedEmployee(employee);
    resetAdvance({
      employeeId: employee.id,
      amount: 0,
      reason: '',
    });
    setIsAdvanceModalOpen(true);
  };

  const handleRepayAdvance = (advanceId: number, amount: number) => {
    repayAdvanceMutation.mutate({ advanceId, amount });
  };

  const calculateTotalAdvances = (advances: Advance[] | undefined) => {
    if (!advances || !Array.isArray(advances)) {
      return 0;
    }
    return advances.reduce((total, advance) => total + (advance.amount - advance.repaid), 0);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load employees. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Salary Management</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage employee salaries and advance payments.
          </p>
        </div>
      </div>

      {/* Employees Salary Overview */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {employees.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No employees found. Add employees to manage their salaries.</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {employees.map((employee: Employee) => (
              <li key={employee.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                        <User className="h-5 w-5 text-green-600" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">{employee.user.name}</div>
                      <div className="text-sm text-gray-500">
                        {employee.type === 'FIXED' ? 'Fixed Salary' : 'Meter-based'} - ₹{employee.rate}
                        {employee.type === 'METER' ? '/meter' : '/month'}
                      </div>
                      <div className="text-sm text-gray-500">
                        Outstanding Advances: ₹{calculateTotalAdvances(employee.advances).toFixed(2)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleWithdrawSalary(employee)}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium text-green-600 bg-green-50 rounded-md hover:bg-green-100"
                    >
                      <DollarSign className="h-4 w-4 mr-1" />
                      Withdraw
                    </button>
                    <button
                      onClick={() => handleAddAdvance(employee)}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Advance
                    </button>
                  </div>
                </div>

                {/* Advances List */}
                {employee.advances && employee.advances.length > 0 && (
                  <div className="mt-4 ml-14">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Advances:</h4>
                    <div className="space-y-2">
                      {employee.advances.map((advance: Advance) => (
                        <div key={advance.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <div className="text-sm">
                            <span className="font-medium">₹{advance.amount}</span>
                            <span className="text-gray-500 ml-2">
                              (Repaid: ₹{advance.repaid}, Outstanding: ₹{(advance.amount - advance.repaid).toFixed(2)})
                            </span>
                            <div className="text-xs text-gray-400">
                              {new Date(advance.createdAt).toLocaleDateString()}
                            </div>
                          </div>
                          {advance.amount > advance.repaid && (
                            <button
                              onClick={() => handleRepayAdvance(advance.id, advance.amount - advance.repaid)}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-600 bg-green-100 rounded hover:bg-green-200"
                            >
                              <Minus className="h-3 w-3 mr-1" />
                              Repay
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Withdraw Salary Modal */}
      {isWithdrawModalOpen && selectedEmployee && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Withdraw Salary for {selectedEmployee.user.name}
              </h3>
              <form onSubmit={handleSalarySubmit(onSalarySubmit)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Amount</label>
                  <input
                    {...registerSalary('amount', { 
                      required: 'Amount is required',
                      min: { value: 0.01, message: 'Amount must be greater than 0' }
                    })}
                    type="number"
                    step="0.01"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="Enter amount to withdraw"
                  />
                  {salaryErrors.amount && (
                    <p className="mt-1 text-sm text-red-600">{salaryErrors.amount.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Reason</label>
                  <textarea
                    {...registerSalary('reason', { required: 'Reason is required' })}
                    rows={3}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="Enter reason for withdrawal"
                  />
                  {salaryErrors.reason && (
                    <p className="mt-1 text-sm text-red-600">{salaryErrors.reason.message}</p>
                  )}
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsWithdrawModalOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={withdrawMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
                  >
                    {withdrawMutation.isPending ? 'Processing...' : 'Withdraw'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add Advance Modal */}
      {isAdvanceModalOpen && selectedEmployee && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Add Advance for {selectedEmployee.user.name}
              </h3>
              <form onSubmit={handleAdvanceSubmit(onAdvanceSubmit)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Amount</label>
                  <input
                    {...registerAdvance('amount', { 
                      required: 'Amount is required',
                      min: { value: 0.01, message: 'Amount must be greater than 0' }
                    })}
                    type="number"
                    step="0.01"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="Enter advance amount"
                  />
                  {advanceErrors.amount && (
                    <p className="mt-1 text-sm text-red-600">{advanceErrors.amount.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Reason</label>
                  <textarea
                    {...registerAdvance('reason', { required: 'Reason is required' })}
                    rows={3}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="Enter reason for advance"
                  />
                  {advanceErrors.reason && (
                    <p className="mt-1 text-sm text-red-600">{advanceErrors.reason.message}</p>
                  )}
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsAdvanceModalOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={addAdvanceMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
                  >
                    {addAdvanceMutation.isPending ? 'Processing...' : 'Add Advance'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Salary;